/**
 * Retired Today URLs (forecast areas spec, section 10.5). The Today pages were retired on
 * 2 Oct 2026; every old URL now answers with one 301 to a page that returns 200.
 *
 * Middleware-safe: imports only the slim route index, the pure link helpers and the pure core of
 * the cross-spot week gate (env plus pure engine core modules), never the full catalogue. `lib/redirects.ts` calls `resolveRetiredTodayRedirect` before its redirect table.
 */
import { crossSpotWeekCertified } from "@/lib/conditions/cross-spot-week-gate-core";
import { conditionsBoardHref } from "@/lib/seo/conditions-links";
import { areaPagePath, boardPathForSport, pageAreaSlugFor } from "./links";
import { AREA_ROUTE_INDEX, SPOT_AREA_INDEX } from "./route-index.generated";
import { AREA_BOARD_SPORTS, type AreaBoardSport, type AreaPageSport } from "./types";

export type LegacyTodayRegion = Readonly<{
  /** The old /today/{region} slug. */
  region: string;
  /** A spot that sat at the heart of the old region; its forecast area decides the target. */
  anchor: string;
  sport: AreaPageSport;
}>;

/** The 20 regions the Today pages served, each with its anchor spot and sport. */
export const LEGACY_TODAY_REGIONS: readonly LegacyTodayRegion[] = Object.freeze([
  { region: "portes-du-soleil", anchor: "morzine", sport: "piste" },
  { region: "espace-killy", anchor: "val-disere", sport: "piste" },
  { region: "three-valleys", anchor: "meribel", sport: "piste" },
  { region: "chamonix-valley", anchor: "chamonix", sport: "piste" },
  { region: "zermatt-cervinia", anchor: "zermatt", sport: "piste" },
  { region: "arlberg", anchor: "st-anton", sport: "piste" },
  { region: "tirol", anchor: "mayrhofen", sport: "piste" },
  { region: "dolomites", anchor: "selva-val-gardena", sport: "piste" },
  { region: "queenstown-wanaka", anchor: "queenstown-remarkables", sport: "piste" },
  { region: "cornwall", anchor: "newquay-fistral", sport: "surf" },
  { region: "north-devon", anchor: "croyde", sport: "surf" },
  { region: "south-wales-gower", anchor: "llangennith", sport: "surf" },
  { region: "south-west-france", anchor: "hossegor", sport: "surf" },
  { region: "basque-country", anchor: "mundaka", sport: "surf" },
  { region: "west-portugal", anchor: "ericeira", sport: "surf" },
  { region: "algarve", anchor: "sagres", sport: "surf" },
  { region: "canary-islands", anchor: "fuerteventura-cotillo", sport: "surf" },
  { region: "morocco-atlantic", anchor: "taghazout", sport: "surf" },
  { region: "bali", anchor: "uluwatu", sport: "surf" },
  { region: "gold-coast", anchor: "snapper-rocks", sport: "surf" },
]);

const LEGACY_TODAY_REGION_BY_SLUG: ReadonlyMap<string, LegacyTodayRegion> = new Map(
  LEGACY_TODAY_REGIONS.map((entry) => [entry.region, entry]),
);

/** True when `areaSlug` is `ancestor` or sits below it in the route index. */
function areaWithin(areaSlug: string, ancestor: string): boolean {
  const seen = new Set<string>();
  let current: string | null = areaSlug;
  while (current && !seen.has(current)) {
    if (current === ancestor) return true;
    seen.add(current);
    current = AREA_ROUTE_INDEX[current]?.parent ?? null;
  }
  return false;
}

/**
 * Where an old /today/{region} URL goes. When an area shares the region's slug, has a page for
 * the sport and contains the anchor spot, that page keeps the old region's whole scope (tirol,
 * dolomites). Otherwise the anchor rule: the page of the anchor spot's leaf area, or of its
 * nearest ancestor with a page for the sport, else the sport's board. Null for unknown regions.
 */
export function legacyTodayRegionDestination(region: string): string | null {
  const entry = LEGACY_TODAY_REGION_BY_SLUG.get(region);
  if (!entry) return null;
  const leaf: string | undefined = SPOT_AREA_INDEX[entry.anchor];
  const sameSlug = AREA_ROUTE_INDEX[entry.region];
  if (sameSlug?.pageSports.includes(entry.sport) && leaf && areaWithin(leaf, entry.region)) {
    return areaPagePath(entry.region, entry.sport);
  }
  const pageArea = leaf ? pageAreaSlugFor(leaf, entry.sport) : null;
  return pageArea ? areaPagePath(pageArea, entry.sport) : boardPathForSport(entry.sport);
}

const GENERIC_PADDLE_BOARD = "/conditions/sup";

/** Each generic board's path and the board sport its This week view reads (spec section 5.13). */
const WEEK_BOARD_SPORT_BY_PATH: ReadonlyMap<string, AreaBoardSport> = new Map(
  AREA_BOARD_SPORTS.map((sport) => [boardPathForSport(sport), sport]),
);

/**
 * The week destination for a sport (spec section 10.5): its board's This week view when the board
 * has one and the cross-spot week gate is open for it (surf stays on the plain board while its
 * gate is closed), else the board. Named paddle profiles fold into the generic paddle board without
 * a week view (never a named profile's week).
 */
export function retiredWeekDestination(
  sport: string | null,
  env: Readonly<Record<string, string | undefined>> = process.env,
): string {
  if (!sport) return "/conditions";
  const board = boardPathForSport(sport);
  const boardSport = WEEK_BOARD_SPORT_BY_PATH.get(board);
  if (!boardSport) return board;
  const namedPaddleProfile = board === GENERIC_PADDLE_BOARD && sport !== "sup";
  if (namedPaddleProfile || !crossSpotWeekCertified(boardSport, env)) return board;
  return conditionsBoardHref(board, { view: "week" });
}

const TODAY_INDEX = /^\/today\/?$/u;
const TODAY_REGION = /^\/today\/([^/]+)\/?$/u;
const TODAY_DEEPER = /^\/today\/.+/u;
const BEST_THIS_WEEK_INDEX = /^\/best-this-week\/?$/u;
const BEST_THIS_WEEK_SPORT = /^\/best-this-week\/([^/]+)\/?$/u;
const BEST_THIS_WEEK_DEEPER = /^\/best-this-week\/.+/u;

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

/**
 * The single-hop destination for a retired Today or weekly URL, or null when the path is not one.
 * The original query is dropped. `/api/today/*` is not handled: it 404s.
 */
export function retiredTodayDestination(pathname: string, search = ""): string | null {
  if (TODAY_INDEX.test(pathname)) {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const sport = params.get("sport")?.trim().toLowerCase() || null;
    if (!sport) return "/conditions";
    const period = params.get("period")?.trim().toLowerCase();
    return period === "week" ? retiredWeekDestination(sport) : boardPathForSport(sport);
  }
  const region = TODAY_REGION.exec(pathname);
  if (region) return legacyTodayRegionDestination(decodeSegment(region[1])) ?? "/conditions";
  if (TODAY_DEEPER.test(pathname)) return "/conditions";

  if (BEST_THIS_WEEK_INDEX.test(pathname)) return "/conditions";
  const weekly = BEST_THIS_WEEK_SPORT.exec(pathname);
  if (weekly) return retiredWeekDestination(decodeSegment(weekly[1]));
  if (BEST_THIS_WEEK_DEEPER.test(pathname)) return "/conditions";
  return null;
}
